/**
 * geo_slug.js — bidirectional helpers for city/state ↔ URL slug.
 *
 * Slug grammar: `leads-{city-kebab}-{state-lower}`
 *   "Orlando, FL"            → "leads-orlando-fl"
 *   "Saint Louis, MO"        → "leads-saint-louis-mo"
 *   "Winston-Salem, NC"      → "leads-winston-salem-nc"   (kept the hyphen)
 *   "St. Petersburg, FL"     → "leads-st-petersburg-fl"   (stripped the dot)
 */
import { US_STATES, US_CITIES_BY_STATE } from "./us_locations";

const STATE_CODES = new Set(US_STATES.map((s) => s.code));

// Iter100 — pre-build a flat list of [{ slug, city, stateCode, stateName }]
// so route resolution + sitemap generation share the same source of truth.
function _kebab(text) {
  return text
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function toSlug(cityWithState) {
  // "Orlando, FL" → "leads-orlando-fl"
  if (!cityWithState) return "";
  const [cityRaw, codeRaw] = cityWithState.split(",").map((s) => s.trim());
  if (!cityRaw || !codeRaw) return "";
  return `leads-${_kebab(cityRaw)}-${codeRaw.toLowerCase()}`;
}

export function fromSlug(slug) {
  // "leads-saint-louis-mo" → { city: "Saint Louis", stateCode: "MO" }
  if (!slug || !slug.startsWith("leads-")) return null;
  const rest = slug.slice("leads-".length);
  const parts = rest.split("-");
  if (parts.length < 2) return null;
  const stateLower = parts[parts.length - 1];
  if (stateLower.length !== 2) return null;
  const stateCode = stateLower.toUpperCase();
  if (!STATE_CODES.has(stateCode)) return null;
  // Reconstruct city from the rest. Match against the curated list
  // (case-insensitive, kebab-equivalent) so we resolve the canonical name.
  const citySlug = parts.slice(0, -1).join("-");
  const candidates = US_CITIES_BY_STATE[stateCode] || [];
  for (const entry of candidates) {
    const [cityName] = entry.split(",").map((s) => s.trim());
    if (_kebab(cityName) === citySlug) {
      const stateName = US_STATES.find((s) => s.code === stateCode)?.name || "";
      return { city: cityName, stateCode, stateName, entry };
    }
  }
  return null;
}

// All curated landing pages, sorted alphabetically by city. Used by
// the sitemap endpoint and the public city directory.
export const ALL_GEO_PAGES = (() => {
  const out = [];
  for (const code of Object.keys(US_CITIES_BY_STATE)) {
    for (const entry of US_CITIES_BY_STATE[code]) {
      const [cityName] = entry.split(",").map((s) => s.trim());
      out.push({
        slug: toSlug(entry),
        city: cityName,
        stateCode: code,
        stateName: US_STATES.find((s) => s.code === code)?.name || "",
        entry,
      });
    }
  }
  out.sort((a, b) => a.city.localeCompare(b.city));
  return out;
})();

// Top N curated landing pages — ordered by state population × city rank.
// Used by the public landing footer to surface the highest-traffic targets.
export function topGeoPages(n = 100) {
  // Each state's first entries are already the most populated cities,
  // so we round-robin across states for diversity.
  const cursors = {};
  const codes = Object.keys(US_CITIES_BY_STATE);
  const result = [];
  while (result.length < n) {
    let added = 0;
    for (const code of codes) {
      const idx = cursors[code] || 0;
      const list = US_CITIES_BY_STATE[code];
      if (idx >= list.length) continue;
      const [cityName] = list[idx].split(",").map((s) => s.trim());
      result.push({
        slug: toSlug(list[idx]),
        city: cityName,
        stateCode: code,
        stateName: US_STATES.find((s) => s.code === code)?.name || "",
        entry: list[idx],
      });
      cursors[code] = idx + 1;
      added += 1;
      if (result.length >= n) break;
    }
    if (added === 0) break;
  }
  return result;
}

// Neighboring cities = other cities in the same state, excluding self.
export function nearbyCities(stateCode, currentCity, limit = 8) {
  const list = US_CITIES_BY_STATE[stateCode] || [];
  return list
    .map((entry) => {
      const [city] = entry.split(",").map((s) => s.trim());
      return { entry, city, slug: toSlug(entry) };
    })
    .filter((x) => x.city !== currentCity)
    .slice(0, limit);
}
